import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import { RRule } from "rrule";
import type { daysOfWeek } from "../deliveryAreaCommons/index.js";
import { RRuleWeekday } from "../deliveryAreaCommons/index.js";

dayjs.extend(utc);

export const getCourierDeliveryAndDispatchDates = ({
  orderCutOffTime,
  dispatchDaysAfterConfirmation,
  deliveryDaysAfterDispatch,
}: {
  orderCutOffTime: string;
  dispatchDaysAfterConfirmation: number;
  deliveryDaysAfterDispatch: number;
}) => {
  const cutOffTimeSplit = orderCutOffTime.toString().split(":");
  const cutOffTime = dayjs()
    .set("hour", parseInt(cutOffTimeSplit[0] ?? ""))
    .set("minutes", parseInt(cutOffTimeSplit[1] ?? ""));
  const currentTime = dayjs();
  const timeDiff = cutOffTime.diff(currentTime, "minute", true);
  let estimatedDispatchDate = dayjs().add(
    timeDiff > 0
      ? dispatchDaysAfterConfirmation
      : dispatchDaysAfterConfirmation + 1,
    "days",
  );

  if (estimatedDispatchDate.day() === 0 || estimatedDispatchDate.day() === 6) {
    estimatedDispatchDate = estimatedDispatchDate.add(
      estimatedDispatchDate.day() === 0 ? 1 : 2,
      "days",
    );
  }
  const estimatedDeliveryDate = estimatedDispatchDate.add(
    deliveryDaysAfterDispatch,
    "days",
  );

  return {
    estimatedDispatchDate: estimatedDispatchDate.startOf("day"),
    estimatedDeliveryDate: estimatedDeliveryDate.startOf("day"),
  };
};

export const getNextCourierDispatchAndDeliveryDates = ({
  courierDaysSchedule,
  orderCutOffTime,
  dispatchDaysAfterConfirmation,
  deliveryDaysAfterDispatch,
  startDateReference,
  allowSaturdayDelivery = false,
}: {
  dispatchDaysAfterConfirmation: number;
  deliveryDaysAfterDispatch: number;
  courierDaysSchedule: { day: daysOfWeek }[];
  orderCutOffTime: string; // only considers hours and minutes
  startDateReference: Date;
  allowSaturdayDelivery?: boolean;
}) => {
  if (!courierDaysSchedule?.length) {
    return getCourierDeliveryAndDispatchDates({
      orderCutOffTime,
      dispatchDaysAfterConfirmation,
      deliveryDaysAfterDispatch,
    });
  }

  const courierDeliveryDaysAsRRuleDays = courierDaysSchedule.map(
    (deliveryAreaDay) => RRuleWeekday[deliveryAreaDay.day],
  );

  const startDate =
    startDateReference && dayjs(startDateReference).isAfter(dayjs().toDate())
      ? startDateReference
      : new Date();
  const currentDateTimeUtc = dayjs(startDate).utc();

  const rule = new RRule({
    // 2 means weekly enum, using as type import not compatible with esm
    freq: 2,
    interval: 1,
    byweekday: courierDeliveryDaysAsRRuleDays,
    dtstart: dayjs(startDate).utc().toDate(),
  });

  const getNextNonWeekendDate = (
    date: dayjs.Dayjs,
    includeSaturday: boolean,
  ) => {
    const saturdayCheck = includeSaturday ? false : date.day() === 6;
    if (date.day() === 0 || saturdayCheck) {
      return getNextNonWeekendDate(date.add(1, "day"), includeSaturday);
    }

    return date;
  };

  const earliestDispatchStartDate = dispatchDaysAfterConfirmation
    ? dayjs(currentDateTimeUtc)
        .add(dispatchDaysAfterConfirmation, "day")
        .startOf("day")
        .toDate()
    : currentDateTimeUtc.startOf("day").toDate();

  // find next dispatch date, get one after if dispatch is same day
  const nextDispatchDate = rule.after(earliestDispatchStartDate, false);

  if (!nextDispatchDate) {
    return;
  }

  const cutOffTimeSplit = orderCutOffTime.toString().split(":");
  const cutOffTime = dayjs(startDate)
    .set("hour", parseInt(cutOffTimeSplit[0] ?? ""))
    .set("minutes", parseInt(cutOffTimeSplit[1] ?? ""));

  // cutoffTime is created as utc, hence hours and minute must be grabbed as utc
  const providedCutoffTimeUtc = cutOffTime
    ? dayjs(cutOffTime).utc()
    : undefined;

  const cutOffHour = providedCutoffTimeUtc?.get("hour") ?? 23;
  const cutOffMinute = providedCutoffTimeUtc?.get("minute") ?? 59;

  const nextDispatchDateCutOff = dayjs(nextDispatchDate)
    .utc()
    // We're currently assuming the cut off is in Europe/London timezone.
    // Ideally suppliers provide a reference timezone we use to determine
    // the cut off
    .tz("Europe/London")
    .subtract(dispatchDaysAfterConfirmation, "days")
    .set("hour", cutOffHour)
    .set("minute", cutOffMinute);

  if (currentDateTimeUtc.isAfter(nextDispatchDateCutOff.utc(), "minute")) {
    const dispatchDateAfterNextOne = rule.after(nextDispatchDate);
    if (!dispatchDateAfterNextOne) {
      return;
    }

    const estimatedDispatchDate = getNextNonWeekendDate(
      dayjs(dispatchDateAfterNextOne).utc().startOf("day"),
      false,
    );
    const estimatedDeliveryDate = getNextNonWeekendDate(
      dayjs(estimatedDispatchDate)
        .add(deliveryDaysAfterDispatch, "day")
        .utc()
        .startOf("day"),
      allowSaturdayDelivery,
    );
    return {
      estimatedDispatchDate,
      estimatedDeliveryDate,
    };
  }

  const estimatedDispatchDate = getNextNonWeekendDate(
    dayjs(nextDispatchDate).utc().startOf("day"),
    false,
  );
  const estimatedDeliveryDate = getNextNonWeekendDate(
    dayjs(estimatedDispatchDate)
      .add(deliveryDaysAfterDispatch, "day")
      .utc()
      .startOf("day"),
    allowSaturdayDelivery,
  );
  return {
    estimatedDispatchDate,
    estimatedDeliveryDate,
  };
};
